import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import {
    deleteInventoryException,
    deleteInventoryBlackout,
    getBaseInventory,
    getInventoryExceptions,
    getInventoryBlackouts,
    updateBaseInventory,
    updateMonthlyInventory,
    updateOrCreateInventoryException,
    updateInventoryException,
    getOperatorInventory,
} from './inventory-actions';

import {
    inventoryBlackoutsAdapter,
    inventoryExceptionsAdapter,
    recurringMonthlyAdapter,
    nonRecurringMonthlyAdapter,
} from './inventory-adapters';

/**
 * @typedef {object} TInventoryState
 * @property {object} blackouts - Inventory Blackouts data
 * @property {Number[]} blackouts.ids - The IDs of the blackouts
 * @property {Object<TBlackouts>|object} blackouts.entities - The blackouts entities
 * @property {boolean} blackouts.isLoading - Is the blackouts data loading?
 * @property {object} exceptions - Inventory Exceptions data
 * @property {Number[]} exceptions.ids - The IDs of the exceptions
 * @property {Object<TInventoryRule>|{}} exceptions.entities - The exceptions entities
 * @property {boolean} exceptions.isLoading - Is the exceptions data loading
 * @property {object} baseInventory - Base Inventory data
 * @property {number} baseInventory.spaces - The number of spaces in the base inventory
 * @property {boolean} baseInventory.isLoading - Is the base inventory data loading
 * @property {object} monthly - Monthly Inventory data
 * @property {object} monthly.recurring - Recurring Monthly Inventory data
 * @property {Number[]} monthly.recurring.ids - The IDs of the recurring monthly inventory
 * @property {Object<TMonthlyInventoryRule>|{}} monthly.recurring.entities - The recurring monthly inventory entities
 * @property {number} monthly.recurring.spaces - The number of spaces in the recurring monthly inventory
 * @property {object} monthly.nonRecurring - Non-Recurring Monthly Inventory data
 * @property {Number[]} monthly.nonRecurring.ids - The IDs of the non-recurring monthly inventory
 * @property {Object<TMonthlyInventoryRule>|{}} monthly.nonRecurring.entities - The non-recurring monthly inventory entities
 * @property {number} monthly.nonRecurring.spaces - The number of spaces in the non-recurring monthly inventory
 * @property {boolean} monthly.isLoading - Is the monthly inventory data loading
 */
const initialState = {
    blackouts: inventoryBlackoutsAdapter.getInitialState({
        isLoading: false,
        meta: {},
    }),
    exceptions: inventoryExceptionsAdapter.getInitialState({
        isLoading: false,
    }),
    baseInventory: {
        spaces: 0,
        isLoading: false,
    },
    monthly: {
        recurring: recurringMonthlyAdapter.getInitialState({
            spaces: 0,
        }),
        nonRecurring: nonRecurringMonthlyAdapter.getInitialState({
            spaces: 0,
        }),
        isLoading: false,
    },
};

export const inventorySlice = createSlice({
    name: 'inventory',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getInventoryExceptions.fulfilled, (state, {payload}) => {
                inventoryExceptionsAdapter.setAll(state.exceptions, payload);
                state.exceptions.isLoading = false;
            })
            .addCase(getInventoryExceptions.pending, (state) => {
                state.exceptions.isLoading = true;
            })
            .addCase(getInventoryExceptions.rejected, (state) => {
                state.exceptions.isLoading = false;
            })
            .addCase(
                updateOrCreateInventoryException.fulfilled,
                (state, {payload}) => {
                    inventoryExceptionsAdapter.upsertOne(
                        state.exceptions,
                        payload
                    );
                }
            )
            .addCase(updateInventoryException.fulfilled, (state, {payload}) => {
                inventoryExceptionsAdapter.upsertOne(state.exceptions, payload);
            })
            .addCase(deleteInventoryException.fulfilled, (state, {payload}) => {
                inventoryExceptionsAdapter.removeOne(state.exceptions, payload);
            })
            .addCase(getBaseInventory.pending, (state) => {
                state.baseInventory.isLoading = true;
            })
            .addCase(getBaseInventory.rejected, (state) => {
                state.baseInventory.isLoading = false;
            })
            .addCase(getInventoryBlackouts.fulfilled, (state, {payload}) => {
                const {data, meta} = payload;

                inventoryBlackoutsAdapter.setAll(state.blackouts, data);

                state.blackouts.meta = meta;
                state.blackouts.isLoading = false;
            })
            .addCase(deleteInventoryBlackout.fulfilled, (state, {payload}) => {
                inventoryBlackoutsAdapter.removeOne(state.blackouts, payload);
                state.blackouts.isLoading = false;
            })
            .addCase(getInventoryBlackouts.rejected, (state, action) => {
                if (action.meta.aborted) {
                    return;
                }

                state.blackouts.isLoading = false;
            })
            .addCase(getOperatorInventory.fulfilled, (state, {payload}) => {
                const {monthlyRecurring, monthlyNonRecurring} = payload;

                recurringMonthlyAdapter.setAll(
                    state.monthly.recurring,
                    monthlyRecurring.rules
                );
                state.monthly.recurring.spaces =
                    monthlyRecurring.totalInventory;
                nonRecurringMonthlyAdapter.setAll(
                    state.monthly.nonRecurring,
                    monthlyNonRecurring.rules
                );
                state.monthly.nonRecurring.spaces =
                    monthlyNonRecurring.totalInventory;

                state.monthly.isLoading = false;
            })
            .addCase(getOperatorInventory.rejected, (state, action) => {
                const {type} = action.meta;

                // eslint-disable-next-line no-undefined
                if (type === undefined) {
                    state.monthly.isLoading = true;
                }
            })
            .addCase(getOperatorInventory.pending, (state, action) => {
                const {type} = action.meta;

                // eslint-disable-next-line no-undefined
                if (type === undefined) {
                    state.monthly.isLoading = true;
                }
            })
            .addCase(updateMonthlyInventory.fulfilled, (state) => {
                state.monthly.isLoading = false;
            })
            .addCase(updateMonthlyInventory.rejected, (state) => {
                state.monthly.isLoading = false;
            })
            .addMatcher(
                isAnyOf(
                    getInventoryBlackouts.pending,
                    deleteInventoryBlackout.pending
                ),
                (state) => {
                    state.blackouts.isLoading = true;
                }
            )
            .addMatcher(
                isAnyOf(
                    updateBaseInventory.fulfilled,
                    getBaseInventory.fulfilled
                ),
                (state, {payload}) => {
                    state.baseInventory.spaces = payload;
                    state.baseInventory.isLoading = false;
                }
            );
    },
});

export default inventorySlice.reducer;
